import React, { useEffect, useState } from 'react';
import { Container } from 'react-bootstrap';
import Filter from '../components/Filter';
import ReleasedTable from '../components/ReleasedTable';
import { GridColDef, GridSortModel } from '@mui/x-data-grid';
import { useRelease } from '../../../apis/release/ReleaseProvider';
import { getPageName } from '../../../locales/translationUtils';
import { useTranslation } from 'react-i18next';
import { useCargoSearchList } from '../../../apis/cargoSearchList/CargoSearchListProvider';
import i18n from '../../../i18n';
import { useNavigate } from 'react-router-dom';
import NavBar, { NavBarItem } from "../../../components/NavBar";
import { downloadContent } from '../../../services/contentService';
import { getListaRelease } from '../../../services/releaseService';
import { exportarParaExcel } from '../../../utils/exportarExcel';
import ExportExcelButton from '../../../components/ExportExcelButton';
import { getUiInterface } from '../../../locales/translationUtils';
import AlertOverlay from '../../../components/Alert';
import CargoContentList from '../../../components/CargoContentList';
import DocumentPreviewModal from '../../../components/DocumentPreviewModal';
import { useDocumentPreview } from '../../../hooks/useDocumentPreview';
import { Content } from '../../Release/types';

const ReleasedListView: React.FC = () => {
  const { t } = useTranslation();
  const [alert, setAlert] = useState<{
      message: string;
      variant: "success" | "danger" | "warning" | "info";
  } | null>(null);
  const navigate = useNavigate();
  const { releases, fetchReleases, apiResponse } = useRelease();
  const { cargosSearchList, fetchCargoSearchList } = useCargoSearchList();
  const { previewState, openPreview, closePreview } = useDocumentPreview();
  const [filters, setFilters] = useState<Record<string, string>>({
    from: '',
    arrivalDate: '',
    pageNum: "0",
    quantityPerPage: "40"
  });
  const [exportando, setExportando] = useState(false);

  const [paginationModel, setPaginationModel] = React.useState({
    pageSize: 40,
    page: 0,
  });

  const [sortModel, setSortModel] = React.useState<GridSortModel>([]);

  useEffect(() => {
    fetchCargoSearchList();
  }, [fetchCargoSearchList]);

  useEffect(() => {
    if (cargosSearchList && cargosSearchList.length > 0) {
      const initialFilters = Object.fromEntries(
        cargosSearchList.map(({ key }: { key: string }) => [key, ""])
      );

      // Campos de data que precisam de período (case-insensitive)
      const dateFieldsLower = ['arrivaldate', 'pu', 'input'];
      
      // Adicionar campos de período para cada campo de data encontrado
      const dateFilters: Record<string, string> = {
        arrivalDateInicio: '',
        arrivalDateFim: '',
      };
      
      cargosSearchList.forEach(({ key }: { key: string }) => {
        if (dateFieldsLower.includes(key.toLowerCase()) && key.toLowerCase() !== 'arrivaldate') {
          dateFilters[`${key}Inicio`] = '';
          dateFilters[`${key}Fim`] = '';
        }
      });

      setFilters(() => ({
        ...initialFilters,
        ...dateFilters,
        from: '',
        arrivalDate: '',
        pageNum: "0",
        quantityPerPage: "40"
      }));
    }
  }, [cargosSearchList]);
  
  useEffect(() => {
    const newFilters = {
      ...filters,
      pageNum: paginationModel.page.toString(),
      quantityPerPage: paginationModel.pageSize.toString(),
      sort: sortModel[0]?.field ?? '',
      order: sortModel[0]?.sort ?? '',
    };
    setFilters(newFilters);
    fetchReleases(newFilters, true);
  }, [paginationModel, sortModel]);

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleSearch = () => {
    const newFilters = { 
      ...filters, 
      pageNum: "0",
      quantityPerPage: "40"
    };
    setFilters(newFilters);
    fetchReleases(newFilters, true);
  };

  /**
   * Exporta para Excel todos os itens que batem com o filtro atual - nao so a
   * pagina visivel. Busca direto do servico, sem tocar no estado do ReleaseProvider
   * que alimenta a grid.
   */
  const handleExportarExcel = async () => {
    setExportando(true);

    try {
      const filtrosParaExportar = {
        ...Object.fromEntries(reorderedFilters),
        pageNum: '0',
        quantityPerPage: '100000',
      };

      const resposta = await getListaRelease(filtrosParaExportar, true);
      const linhas = (resposta?.data?.values ?? []).map((row: any) => {
        const info = row?.carrierInformation?.[0];
        const formataData = (valor: unknown) => {
          if (!valor) return '';
          const data = new Date(valor as string);
          return isNaN(data.getTime()) ? '' : dateFormatter.format(data);
        };

        return {
          [t('release.fields.id')]: row.release?.id ?? '',
          [t('release.fields.from')]: row.fromIdentification ?? '',
          [t('release.fields.client')]: row.clientName ?? '',
          [t('release.fields.supplier')]: row.supplierName ?? '',
          [t('release.fields.wrNumber')]: row.wrNumber ?? '',
          [t('release.fields.poNumber')]: row.poNumber ?? '',
          [t('release.fields.pieces')]: row.pieces ?? '',
          [t('release.fields.weight')]: row.weight ?? '',
          [t('release.fields.dawb')]: row.dawb ?? '',
          [t('release.fields.invoice')]: info?.invoice ?? '',
          [t('release.fields.pu')]: formataData(row.pu),
          [t('release.fields.input')]: formataData(row.input),
          [t('release.fields.to')]: row.release?.to ?? '',
          [t('release.fields.releasedBy')]: row.release?.releasedBy ?? '',
          [t('release.fields.released')]: row.release?.releasedFormatted ?? '',
        };
      });

      if (!exportarParaExcel(linhas, 'released')) {
        setAlert({ message: getUiInterface('exportEmptyResult'), variant: 'warning' });
      }
    } catch (error) {
      setAlert({ message: getUiInterface('exportError'), variant: 'danger' });
    } finally {
      setExportando(false);
    }
  };
  
  const handleDownload = async (content: Pick<Content, 'id' | 'description'>) => {
    try {
      const response = await downloadContent(content.id);
      
      const downloadUrl = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `${content.description}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
        setAlert({ message: `${t('release.labels.downloadError')} ${content.description}`, variant: "danger" });
    }
  };

  // E5: visualizar PDF/imagem direto no navegador, sem baixar antes.
  const handleView = async (content: Pick<Content, 'id' | 'description'>) => {
    try {
      await openPreview(content);
    } catch (error) {
      setAlert({ message: `${t('release.labels.downloadError')} ${content.description}`, variant: "danger" });
    }
  };

  const navItems: NavBarItem[] = [
    { label: t('release.labels.home'), route: "/", iconName: "house-door" },
    { label: t('release.labels.list'), route: "/release", iconName: "table" },
  ];

  const dateFormatter = new Intl.DateTimeFormat(i18n?.language ?? 'pt');

  const columns: GridColDef[] = [
    { 
      field: 'id', 
      headerName: t('release.fields.id'), 
      width: 80, 
      renderCell: (params) => (
          <span
              style={{ color: "black", cursor: "pointer" }}
              onClick={() => navigate(`/release/released/${params.row.release?.id}`)}>
              {params.row.release?.id}
          </span>
      ),
    },
    { field: 'fromIdentification', headerName: t('release.fields.from'), width: 100 },
    { field: 'clientName', headerName: t('release.fields.client'), width: 120 },
    { field: 'supplierName', headerName: t('release.fields.supplier'), width: 350 },
    { field: 'wrNumber', headerName: t('release.fields.wrNumber'), width: 120 },
    { field: 'poNumber', headerName: t('release.fields.poNumber'), width: 120 },
    {
      field: 'piecesWeight',
      headerName: t('release.fields.piecesWeight'),
      width: 120,
      renderCell: (params: any) => {
        const { pieces, weight } = params.row;
        return `${pieces} / ${weight}`;
      },
    },
    { field: 'dawb', headerName: t('release.fields.dawb'), width: 120 },
    {
      field: 'invoice',
      headerName: t('release.fields.invoice'),
      width: 220,
      renderCell: (params: any) => {
        const info = params?.row?.carrierInformation?.[0];
        const invoice = info?.invoice ?? '-';
        const documentKey = info?.document?.key ?? '-';
        return `${invoice} / ${documentKey}`;
      }
    },
    {
      field: 'pu',
      headerName: t('release.fields.pu'),
      type: "date",
      width: 120,
      valueFormatter: (params: any) => {
        if (!params) return null;
        const date = new Date(params);
        return isNaN(date.getTime()) ? null : dateFormatter.format(date);
      }
    },
    {
      field: 'input',
      headerName: t('release.fields.input'),
      type: "date",
      width: 120,
      valueFormatter: (params: any) => {
        if (!params) return null;
        const date = new Date(params);
        return isNaN(date.getTime()) ? null : dateFormatter.format(date);
      }
    },
    {
      field: 'content',
      headerName: t('release.fields.content'),
      width: 500,
      minWidth: 200,
      renderCell: (params: any) => (
        <CargoContentList
          cargoContents={params.row?.cargoContents ?? []}
          onDownload={handleDownload}
          downloadTitle={t('release.labels.clickToDownload')}
          expandTitle={t('release.labels.showAllFiles')}
          onView={handleView}
          viewTitle={t('release.labels.clickToView')}
        />
      )
    },
    {
      field: 'to', headerName: t('release.fields.to'), width: 180, renderCell: (params: any) => {
        return params?.row?.release?.to;
      }
    },
    {
      field: 'releasedBy', headerName: t('release.fields.releasedBy'), width: 160, renderCell: (params: any) => {
        return params?.row?.release?.releasedBy;
      }
    },
    {
      field: 'releasedFormatted',
      headerName: t('release.fields.released'),
      width: 120,
      renderCell: (params: any) => {
        return params?.row?.release?.releasedFormatted;
      }
    }
  ];

  const dynamicFilters = Object.entries(filters).filter(
    ([key]) => key !== 'from' && key !== 'arrivalDate'
  );

  const reorderedFilters = [
    ...dynamicFilters,
    ['from', filters.from],
    ['arrivalDate', filters.arrivalDate],
  ];

  return (
    <Container
      fluid
      style={{ height: 'calc(100vh - 150px)', display: 'flex', flexDirection: 'column' }}
    >
      <NavBar items={navItems} />
      {alert && (
          <AlertOverlay
              message={alert.message}
              variant={alert.variant}
              onClose={() => setAlert(null)}
          />
      )}
      <div className="mb-3"></div>
      <h2>{getPageName('released')}</h2>
      <div className="mb-1"></div>
      <Filter
        filters={Object.fromEntries(reorderedFilters)}
        onFilterChange={handleFilterChange}
        onSearch={handleSearch}
      />
      <div className="d-flex justify-content-end mb-2">
        <ExportExcelButton onClick={handleExportarExcel} exportando={exportando} />
      </div>
      <div style={{ flex: 1, minHeight: 0 }}>
        <ReleasedTable
          apiResponse={apiResponse}
          rows={releases ?? []}
          columns={columns}
          paginationModel={paginationModel}
          setPaginationModel={setPaginationModel}
          sortModel={sortModel}
          onSortModelChange={setSortModel}
        />
      </div>

      <DocumentPreviewModal state={previewState} onClose={closePreview} />
    </Container>
  );
};

export default ReleasedListView;
